import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'systolic', 'diastolic', 'pulse',
    'readingDisplay', 'categoryContainer', 'categoryDot', 'categoryName',
    'categoryDescription', 'actionGuidance'
  ]

  connect() {
    this.calculate()
  }

  calculate() {
    const systolic = parseInt(this.systolicTarget.value, 10) || 120
    const diastolic = parseInt(this.diastolicTarget.value, 10) || 80

    this.readingDisplayTarget.textContent = `${systolic}/${diastolic}`

    const category = this.getCategory(systolic, diastolic)
    this.updateDisplay(category)
  }

  getCategory(systolic, diastolic) {
    // AHA Blood Pressure Categories
    if (systolic > 180 || diastolic > 120) {
      return {
        name: 'HYPERTENSIVE CRISIS',
        color: 'red-700',
        borderColor: 'border-red-700',
        bgColor: 'bg-red-50',
        description: 'This reading indicates a hypertensive crisis. Wait 5 minutes and retest. ' +
          'If still this high, seek immediate medical attention.',
        action: 'SEEK IMMEDIATE MEDICAL ATTENTION. Call your doctor or 911 if experiencing symptoms ' +
          'like chest pain, shortness of breath, or neurological symptoms.'
      }
    } if (systolic >= 140 || diastolic >= 90) {
      return {
        name: 'STAGE 2 HYPERTENSION',
        color: 'red-500',
        borderColor: 'border-red-500',
        bgColor: 'bg-red-50',
        description: 'Your blood pressure is significantly elevated. This level typically requires medication along with lifestyle changes.',
        action: 'Consult your healthcare provider. Medication is often recommended at this level, along with lifestyle modifications.'
      }
    } if (systolic >= 130 || diastolic >= 80) {
      return {
        name: 'STAGE 1 HYPERTENSION',
        color: 'orange-500',
        borderColor: 'border-orange-500',
        bgColor: 'bg-orange-50',
        description: 'Your blood pressure is elevated. Lifestyle changes are recommended, ' +
          'and medication may be considered based on cardiovascular risk.',
        action: 'Focus on lifestyle changes: reduce sodium, increase exercise, manage stress. ' +
          'Follow up with your doctor to assess cardiovascular risk.'
      }
    } if (systolic >= 120 && diastolic < 80) {
      return {
        name: 'ELEVATED',
        color: 'yellow-500',
        borderColor: 'border-yellow-500',
        bgColor: 'bg-yellow-50',
        description: 'Your blood pressure is higher than optimal but not yet in the hypertension range. ' +
          'Lifestyle changes can help prevent progression.',
        action: 'Adopt healthy lifestyle habits now to prevent progression. Reduce sodium, ' +
          'exercise regularly, maintain healthy weight. Recheck in 3-6 months.'
      }
    }
    return {
      name: 'NORMAL',
      color: 'green-500',
      borderColor: 'border-green-500',
      bgColor: 'bg-green-50',
      description: 'Your blood pressure is in the optimal range. Continue healthy lifestyle habits to maintain these levels.',
      action: 'Maintain healthy habits. Recheck in 1-2 years or as recommended by your healthcare provider.'
    }
  }

  updateDisplay(category) {
    this.categoryDotTarget.className = `w-4 h-4 bg-${category.color}`
    this.categoryNameTarget.textContent = category.name
    this.categoryDescriptionTarget.textContent = category.description
    this.actionGuidanceTarget.textContent = category.action
    this.categoryContainerTarget.className = `p-6 ${category.borderColor} border-2 ${category.bgColor}`
  }
}
